"use client";

import { useState, FormEvent } from "react";
import { useRouter } from "next/navigation";
import Image from "next/image";
import toast from "react-hot-toast";

import NavBar from "@/app/components/NavBar";
import Footer from "@/app/components/Footer";
import { stringOrFallback } from "@/app/pages/lib/contentHelpers";

interface PaymentFormData {
  amount: string;
  description: string;
  email: string;
  phone?: string;
  acceptTerms: boolean;
  acceptPrivacy: boolean;
}

interface PaymentFormClientProps {
  locale: string;
  content: Record<string, unknown>;
}

export default function PaymentFormClient({ locale, content }: PaymentFormClientProps) {
  const router = useRouter();
  const t = (key: string, fallback: string) => stringOrFallback(content[key], fallback);

  const [loading, setLoading] = useState(false);
  const [smsLoading, setSmsLoading] = useState(false);
  const [formData, setFormData] = useState<PaymentFormData>({
    amount: "",
    description: "Opłata za rezerwację/wizytę stomatologiczną",
    email: "",
    phone: "",
    acceptTerms: false,
    acceptPrivacy: false,
  });

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (!formData.acceptTerms) {
        toast.error(t("errorAcceptTerms", "Musisz zaakceptować regulamin"));
        setLoading(false);
        return;
      }

      if (!formData.acceptPrivacy) {
        toast.error(t("errorAcceptPrivacy", "Musisz zaakceptować politykę prywatności"));
        setLoading(false);
        return;
      }

      const amountInCents = Math.round(parseFloat(formData.amount) * 100);

      if (!Number.isFinite(amountInCents) || amountInCents < 100) {
        toast.error(t("errorMinAmount", "Minimalna kwota to 1 PLN"));
        setLoading(false);
        return;
      }

      if (amountInCents > 100000000) {
        toast.error(t("errorMaxAmount", "Maksymalna kwota to 1,000,000 PLN"));
        setLoading(false);
        return;
      }

      const response = await fetch("/api/payments/create", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          amount: amountInCents,
          currency: "PLN",
          description: formData.description,
          buyer: {
            email: formData.email,
            phone: formData.phone
              ? {
                  prefix: "+48",
                  number: formData.phone.replace(/\D/g, "").slice(-9),
                }
              : undefined,
          },
          externalId: `dental_${Date.now()}`,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Błąd podczas inicjalizacji płatności");
      }

      if (data.redirectUrl) {
        window.location.href = data.redirectUrl;
      } else {
        throw new Error(t("errorPayment", "Brak linku do płatności"));
      }
    } catch (error) {
      console.error("Payment error:", error);
      toast.error(
        error instanceof Error ? error.message : t("errorGeneral", "Błąd podczas inicjalizacji płatności"),
      );
      setLoading(false);
    }
  };

  const handleSmsSubmit = async () => {
    setSmsLoading(true);

    try {
      if (!formData.email || !formData.email.trim()) {
        toast.error(t("errorEmailRequired", "Email jest wymagany"));
        setSmsLoading(false);
        return;
      }

      if (!formData.phone || !formData.phone.trim()) {
        toast.error(t("errorPhoneRequired", "Numer telefonu jest wymagany do wysyłki SMS"));
        setSmsLoading(false);
        return;
      }

      if (!formData.acceptTerms) {
        toast.error(t("errorAcceptTerms", "Musisz zaakceptować regulamin"));
        setSmsLoading(false);
        return;
      }

      if (!formData.acceptPrivacy) {
        toast.error(t("errorAcceptPrivacy", "Musisz zaakceptować politykę prywatności"));
        setSmsLoading(false);
        return;
      }

      if (!formData.description || !formData.description.trim()) {
        toast.error(t("errorDescriptionRequired", "Opis płatności jest wymagany"));
        setSmsLoading(false);
        return;
      }

      const amountInCents = Math.round(parseFloat(formData.amount) * 100);

      if (!Number.isFinite(amountInCents) || amountInCents < 100) {
        toast.error(t("errorMinAmount", "Minimalna kwota to 1 PLN"));
        setSmsLoading(false);
        return;
      }

      if (amountInCents > 100000000) {
        toast.error(t("errorMaxAmount", "Maksymalna kwota to 1,000,000 PLN"));
        setSmsLoading(false);
        return;
      }

      const response = await fetch("/api/payments/sms-link", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          amount: amountInCents,
          currency: "PLN",
          description: formData.description,
          buyer: {
            email: formData.email,
            phone: {
              prefix: "+48",
              number: formData.phone.replace(/\D/g, "").slice(-9),
            },
          },
          externalId: `dental_${Date.now()}`,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Błąd podczas wysyłania linku SMS");
      }

      if (data.success) {
        toast.success(
          t("smsSent", "Link do płatności wysłany SMS-em na numer ") + formData.phone,
        );
      } else {
        toast.error(
          t("smsError", "Nie udało się wysłać SMS-a. Spróbuj ponownie lub użyj opcji Przejdź do płatności."),
        );
      }
    } catch (error) {
      console.error("SMS link error:", error);
      toast.error(
        error instanceof Error ? error.message : t("errorGeneral", "Błąd podczas wysyłania linku SMS"),
      );
      setSmsLoading(false);
    } finally {
      setSmsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  return (
    <>
      <NavBar locale={locale} />
      <div className="min-h-screen bg-gradient-to-b from-sky-50 to-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-md">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2 text-center">
              {t("title", "Płatność online")}
            </h1>
            <p className="text-gray-600 text-center mb-8">
              {t("subtitle", "Wypełnij formularz, aby przejść do płatności")}
            </p>

            <div className="flex justify-center mb-6">
              <Image 
                src="/images/baner.png" 
                alt="Visa Mastercard" 
                width={200}
                height={48}
                className="h-12 object-contain"
              />
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-2">
                  {t("amountRequired", "Kwota (PLN) *")}
                </label>
                <input
                  type="number"
                  id="amount"
                  name="amount"
                  required
                  min="1"
                  max="1000000"
                  step="0.01"
                  value={formData.amount}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                  placeholder={t("amountPlaceholder", "100.00")}
                  disabled={loading || smsLoading}
                />
                <p className="mt-1 text-sm text-gray-500">
                  {t("amountMin", "Minimalna kwota: 1 PLN")}
                </p>
              </div>

              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                  {t("descriptionRequired", "Opis płatności *")}
                </label>
                <textarea
                  id="description"
                  name="description"
                  required
                  rows={3}
                  value={formData.description}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                  placeholder={t("descriptionPlaceholder", "Np. Opłata za wizytę")}
                  disabled={loading || smsLoading}
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  {t("emailRequired", "Email *")}
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                  placeholder={t("emailPlaceholder", "twoj@email.pl")}
                  disabled={loading || smsLoading}
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  {t("phone", "Telefon (opcjonalnie)")}
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                  placeholder={t("phonePlaceholder", "123 456 789")}
                  disabled={loading || smsLoading}
                />
              </div>

              <div className="space-y-3 border-t border-gray-200 pt-6">
                <div className="flex items-start">
                  <input
                    type="checkbox"
                    id="acceptTerms"
                    name="acceptTerms"
                    checked={formData.acceptTerms}
                    onChange={handleChange}
                    disabled={loading || smsLoading}
                    className="mt-1 h-4 w-4 text-sky-600 border-gray-300 rounded focus:ring-sky-500"
                    required
                  />
                  <label htmlFor="acceptTerms" className="ml-3 text-sm text-gray-700">
                    {t("acceptTermsPrefix", "Akceptuję")}{" "}
                    <a
                      href={`/${locale}/terms-of-service`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sky-600 hover:text-sky-700 underline"
                    >
                      {t("termsLink", "Regulamin świadczenia usług")}
                    </a>
                    {" *"}
                  </label>
                </div>

                <div className="flex items-start">
                  <input
                    type="checkbox"
                    id="acceptPrivacy"
                    name="acceptPrivacy"
                    checked={formData.acceptPrivacy}
                    onChange={handleChange}
                    disabled={loading || smsLoading}
                    className="mt-1 h-4 w-4 text-sky-600 border-gray-300 rounded focus:ring-sky-500"
                    required
                  />
                  <label htmlFor="acceptPrivacy" className="ml-3 text-sm text-gray-700">
                    {t("acceptPrivacyPrefix", "Akceptuję")}{" "}
                    <a
                      href={`/${locale}/privacy-policy`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sky-600 hover:text-sky-700 underline"
                    >
                      {t("privacyLink", "Politykę prywatności")}
                    </a>
                    {" *"}
                  </label>
                </div>
              </div>

              <div className="space-y-3">
                <button
                  type="submit"
                  disabled={loading || smsLoading || !formData.acceptTerms || !formData.acceptPrivacy}
                  className="w-full bg-sky-600 text-white font-semibold py-3 px-6 rounded-lg hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                >
                  {loading && (
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  )}
                  {loading ? t("processing", "Przetwarzanie...") : t("submitButton", "Przejdź do płatności")}
                </button>

                <button
                  type="button"
                  onClick={handleSmsSubmit}
                  disabled={loading || smsLoading || !formData.acceptTerms || !formData.acceptPrivacy}
                  className="w-full bg-emerald-600 text-white font-semibold py-3 px-6 rounded-lg hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                >
                  {smsLoading ? (
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
                    </svg>
                  )}
                  {smsLoading ? t("sending", "Wysyłanie...") : t("sendSmsButton", "Wyślij link SMS-em")}
                </button>
              </div>

              <p className="text-xs text-gray-500 text-center mt-4">
                {t("secureNote", "Płatności obsługiwane przez PayNow. Twoje dane są bezpieczne.")}
              </p>
            </form>
          </div>

          <div className="mt-6 text-center">
            <button onClick={() => router.back()} className="text-sky-600 hover:text-sky-700 font-medium">
              ← {t("backButton", "Powrót")}
            </button>
          </div>
        </div>
      </div>
      <Footer locale={locale} />
    </>
  );
}
